import { get } from 'lodash';

import { APIcaller, APIcallerv2 } from './APIcaller';
import {
  clearAppSessionStorage,
  isMosAuthProvider,
  setLegacyAuthProvider,
} from '@commons/auth/session';
import { config } from '../config';
import { logoutMos } from './mosAuth';
import { setLocalStorageItem } from '@commons/utils/localStorage';
import axios from '../core/http';

const baseApiUrl = `${config.baseApiUrl}/v1`;

function login(email, password) {
  return axios
    .post(
      `${baseApiUrl}/signin/login`,
      {
        email: email,
        password: password,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    .then((response) => {
      setLocalStorageItem('loopbackAccessToken', response.data.id);
      setLocalStorageItem('userId', response.data.userId);
      setLegacyAuthProvider();
      return response.data.user;
    })
    .catch((error) => {
      throw error.response;
    });
}

/**
 * Does not use APIcaller, which reads the token asynchronously: callers clear the session storage
 * right after dispatching the logout, so the token must be read synchronously.
 */
function logout() {
  if (isMosAuthProvider()) {
    return logoutMos().then(() => true);
  }

  return axios
    .post(
      `${baseApiUrl}/signout/logout`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('loopbackAccessToken'),
        },
      },
    )
    .then((response) => {
      clearAppSessionStorage();
      return response.data;
    })
    .catch((error) => {
      // Do not consider unauthorized error as a failure
      if (get(error.response, 'status') === 401) {
        clearAppSessionStorage();
        return true;
      }

      throw error;
    });
}

function getUserRights(accountId) {
  return APIcaller.apiGet(`/users/${accountId}/rights`);
}

function changePassword(oldPassword, newPassword) {
  return APIcaller.apiPut('/users/change-password', { oldPassword, newPassword });
}

/**
 * Does not use APIcaller, which would send the session token instead of the password-reset one.
 */
function changePasswordFromResetToken(accessToken, new_password) {
  return axios
    .put(
      baseApiUrl + '/users/reset-password',
      {
        newPassword: new_password,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: accessToken,
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      throw error.response;
    });
}

function resetPassword(email) {
  return axios
    .post(
      baseApiUrl + '/users/forgot-password',
      {
        email: email,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const isAuthenticated = async () => {
  try {
    return APIcallerv2.apiGet(`/users/profile`);
  } catch (err) {
    throw err;
  }
};

export function getAccountById(accountId) {
  return APIcaller.apiGet(`/users/${accountId}`);
}

export const getAccountsByClientId = (clientId, withoutInpulseRole = false) =>
  APIcaller.apiGet(`/users/list/${clientId}`, { withoutInpulseRole });

export function patchAccountById(accountId, account) {
  return axios
    .patch(`${baseApiUrl}/users/${accountId}`, account, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

export function updateUserPassword(accountId, newPassword) {
  return axios
    .post(`${baseApiUrl}/users/${accountId}/reset-password`, {
      newPassword,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then((response) => response.data)
    .then((result) => {
      if (result.error && result.error.statusCode === 400) {
        throw result.error;
      }
      return result;
    })
    .catch((error) => {
      throw error.response;
    });
}

export function deleteAccountByIds(accountIds) {
  return axios
    .post(`${baseApiUrl}/users/deleteMany`, {
      accountIds,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

export function getStoresOfAccount(accountId) {
  return APIcaller.apiGet(`/users/${accountId}/stores`);
}

export function dissociateStoresToAccount(accountId, storeIds) {
  return axios
    .post(`${baseApiUrl}/users/${accountId}/stores/deleteMany`, {
      storeIds,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

export function associateStoresToAccount(accountId, storeIds) {
  return axios
    .post(`${baseApiUrl}/users/${accountId}/stores`, {
      storeIds,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

export function updateAccountsByIds(userIds, fields) {
  const payload = {
    userIds: userIds,
    fields: fields,
  };
  return axios
    .patch(`${baseApiUrl}/backoffice/users`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

export const switchAccount = (targetAccountId) =>
  APIcaller.apiPost('/users/switch/', { targetAccountId });

export const deleteStoreAssociationByAccountIds = (storeId, accountIds) =>
  APIcaller.apiDelete(`/users/store/${storeId}/deleteMany`, { accountIds });

export const createStoreAssociationByAccountIds = (storeId, accountIds) =>
  APIcaller.apiPost(`/users/store/${storeId}/createMany`, { accountIds });

export const deleteAccessTokenById = ({ accessToken }) =>
  APIcallerv2.apiDelete(`/users/access-token`, { accessToken });

export const user = {
  login,
  logout,
  getUserRights,
  resetPassword,
  getAccountById,
  changePassword,
  isAuthenticated,
  patchAccountById,
  getStoresOfAccount,
  deleteAccountByIds,
  updateUserPassword,
  getAccountsByClientId,
  associateStoresToAccount,
  dissociateStoresToAccount,
  changePasswordFromResetToken,
  updateAccountsByIds,
  switchAccount,
  deleteStoreAssociationByAccountIds,
  createStoreAssociationByAccountIds,
  deleteAccessTokenById,
};
